import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  publishReplay,
  refCount,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Snippet } from '../models/snippet.model';

@Injectable({
  providedIn: 'root',
})
export class SnippetService {
  data = new BehaviorSubject(null);
  saved = new BehaviorSubject<any>(null);
  slug = '/snippets';

  allScenarios?: Observable<any[]> | null = null;
  publicBlocks?: Observable<any[]> | null = null;
  windowBlocks?: Observable<any[]> | null = null;
  publicStepTemplates?: Observable<any[]> | null = null;
  publicScenarioTemplates?: Observable<any[]> | null = null;

  constructor(private http: HttpClient) {}
  setData(data: any) {
    this.saved.next(false);
    this.data.next(data);
  }

  fetchAll(): Observable<any[]> {
    if (!this.allScenarios) {
      this.allScenarios = this.http
        .get<any>(environment.apiUrl + this.slug, {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData._embedded.snippets.forEach((item: any) => {
              item.snippet_json = JSON.parse(item.snippet_json);

              item.tags = JSON.parse(item.tags);
              returnArray.push(item);
            });
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.allScenarios;
  }

  fetchPublicBlocks(): Observable<any[]> {
    if (!this.publicBlocks) {
      this.publicBlocks = this.http
        .get<any>(environment.apiUrl + '/public_blocks', {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData._embedded.public_blocks.forEach((item: any) => {
              item.snippet_json = JSON.parse(item.snippet_json);

              item.tags = JSON.parse(item.tags);
              returnArray.push(item);
            });
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.publicBlocks;
  }

  fetchPublicComponents(): Observable<any[]> {
    if (!this.publicBlocks) {
      this.publicBlocks = this.http
        .get<any>(environment.apiUrl + '/public_components', {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData._embedded.public_components.forEach((item: any) => {
              item.snippet_json = JSON.parse(item.snippet_json);

              item.tags = JSON.parse(item.tags);
              returnArray.push(item);
            });
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.publicBlocks;
  }

  fetchWindowBlocks(): Observable<any[]> {
    if (!this.windowBlocks) {
      let params = new HttpParams();
      params = params.append('window', '1');

      this.windowBlocks = this.http
        .get<any>(environment.apiUrl + '/public_blocks', {
          responseType: 'json',
          params,
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData._embedded.public_blocks.forEach((item: any) => {
              item.snippet_json = JSON.parse(item.snippet_json);
              item.tags = JSON.parse(item.tags);
              returnArray.push(item);
            });
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.windowBlocks;
  }

  fetchPublicStepTemplates(): Observable<any[]> {
    if (!this.publicStepTemplates) {
      this.publicStepTemplates = this.http
        .get<any>(environment.apiUrl + '/public_step_templates', {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData._embedded.public_step_templates.forEach(
              (item: any) => {
                item.snippet_json = JSON.parse(item.snippet_json);
                item.tags = JSON.parse(item.tags);
                returnArray.push(item);
              }
            );
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.publicStepTemplates;
  }



  fetchScenarioTemplates(): Observable<any[]> {
    if (!this.publicScenarioTemplates) {
      this.publicScenarioTemplates = this.http
        .get<any>(environment.apiUrl + '/public_scenario_templates', {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: any[] = [];
            responseData._embedded.public_scenario_templates.forEach(
              (item: any) => {
                returnArray.push(item);
              }
            );
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.publicScenarioTemplates;
  }
  

  clearCache() {
    this.allScenarios = null;
    this.publicBlocks = null;
    this.publicStepTemplates = null;
  }

  fetch(id: number) {
    return this.http
      .get<any>(environment.apiUrl + this.slug + '/' + id, {
        responseType: 'json',
      })
      .pipe(
        map((responseData) => {
          const item = new Snippet(
            +responseData.scenario_id,
            +responseData.user_id,
            responseData.title,
            responseData.type,
            JSON.parse(responseData.snippet_json),
            responseData.created,
            responseData.modified
          );
          return item;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  create(
    title: string,
    type: string,
    snippet_json: string,
    scenarioId: string,
    tags: string
  ) {
    this.clearCache();
    const payload = {
      title,
      type,
      snippet_json,
      scenario_id: scenarioId,
      tags,
    };
    return this.http.post<Snippet>(environment.apiUrl + this.slug, payload, {
      observe: 'response',
    });
  }

  update(
    id: number,
    title: string,
    snippet_json: string,
    scenarioId: string,
    tags: string
  ) {
    this.clearCache();
    const payload = { title, snippet_json, scenario_id: scenarioId, tags };
    this.saved.next(true);
    return this.http.patch<Snippet>(
      environment.apiUrl + this.slug + '/' + id,
      payload,
      {
        observe: 'response',
      }
    );
  }

  delete(id: number) {
    this.clearCache();
    return this.http.delete<{ name: string }>(
      environment.apiUrl + this.slug + '/' + id
    );
  }

  parseTags(tags: any) {
    let parsedTags: any = [];

    tags.forEach((tag: any) => {
      parsedTags.push({ value: tag.value });
    });

    return parsedTags;
  }
}
